import React from "react";
import { Check, PackageCheck, Truck, Warehouse, Inbox } from "lucide-react";
import { WORKFLOW_STEPS } from "../lib/constants";

const STEP_ICONS = {
  Received: Inbox,
  Stocked: Warehouse,
  Destocked: Truck,
  Delivered: PackageCheck,
};

/**
 * Visual 4-step horizontal progress bar: Receive → Stock → Destock → Deliver.
 * - past stages: solid green with check
 * - current stage: blue, scaled
 * - upcoming stages: slate-400 outline
 *
 * For Installed/Returned/Requested-by-Crew (off-track stages) we still highlight
 * the closest match so the runner knows where the item ended up.
 */
export default function WorkflowProgressBar({ stage = "Received", testid = "workflow-progress" }) {
  // Map off-track stages to their closest position on the linear bar
  const positionStage = (() => {
    if (stage === "Installed") return "Delivered";
    if (stage === "Returned") return "Delivered";
    if (stage === "Requested by Crew") return "Received";
    return stage;
  })();

  const currentIdx = Math.max(0, WORKFLOW_STEPS.indexOf(positionStage));
  const isCompleted = stage === "Installed";

  return (
    <div className="w-full" data-testid={testid}>
      <div className="flex items-center gap-1 sm:gap-2">
        {WORKFLOW_STEPS.map((s, idx) => {
          const Icon = STEP_ICONS[s];
          const past = idx < currentIdx || (idx === currentIdx && isCompleted);
          const current = idx === currentIdx && !isCompleted;
          const upcoming = !past && !current;
          return (
            <React.Fragment key={s}>
              <div
                className="flex flex-col items-center flex-1 min-w-0"
                data-testid={`${testid}-step-${s}`}
                data-step-state={past ? "past" : current ? "current" : "upcoming"}
              >
                <div
                  className={`h-10 w-10 sm:h-12 sm:w-12 grid place-items-center rounded-sm border-2 transition-colors ${
                    past
                      ? "bg-green-600 border-green-800 text-white"
                      : current
                      ? "bg-blue-600 border-blue-800 text-white scale-110"
                      : "bg-white border-slate-300 text-slate-400"
                  }`}
                >
                  {past ? (
                    <Check strokeWidth={3} className="h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                    <Icon strokeWidth={2.5} className="h-5 w-5 sm:h-6 sm:w-6" />
                  )}
                </div>
                <div
                  className={`mt-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wide text-center ${
                    current
                      ? "text-blue-700"
                      : past
                      ? "text-green-700"
                      : "text-slate-400"
                  }`}
                >
                  {idx + 1}. {s}
                </div>
              </div>
              {idx < WORKFLOW_STEPS.length - 1 && (
                <div
                  className={`flex-shrink-0 h-1 w-4 sm:w-8 rounded-sm -mt-5 ${
                    idx < currentIdx ? "bg-green-600" : "bg-slate-200"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {(stage === "Installed" || stage === "Returned" || stage === "Requested by Crew") && (
        <div
          className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border-2 ${
            stage === "Installed"
              ? "bg-green-100 text-green-800 border-green-700"
              : stage === "Returned"
              ? "bg-rose-100 text-rose-800 border-rose-700"
              : "bg-purple-100 text-purple-800 border-purple-700"
          }`}
          data-testid={`${testid}-extra-${stage}`}
        >
          {stage === "Installed" ? "✓ Installed" : stage === "Returned" ? "↩ Returned" : "⚡ Crew Request"}
        </div>
      )}
    </div>
  );
}
